import { Card } from "@tremor/react";
import { Link } from "react-router-dom";
import { Download, FileText, ExternalLink } from "lucide-react";
import { useReports, reportDownloadUrl, type Report } from "@/lib/queries";

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtBytes(n: number | null): string {
  if (n === null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function Reports() {
  const list = useReports(50);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
        <p className="text-sm text-muted-foreground">
          Customer-facing PDF reports generated from audits via the
          <code className="ml-1 rounded bg-muted px-1 font-mono">/report</code> slash
          command in chat. Each row is one PDF, downloadable with the
          customer's branding and the NWS-styled body. Scoped to your
          tenant — a Cambridge admin (when one exists) sees only Cambridge-
          produced reports, not NRG's.
        </p>
      </div>

      <Card className="p-0 overflow-hidden">
        {list.isLoading && (
          <div className="px-4 py-6 text-sm text-muted-foreground">Loading…</div>
        )}
        {list.data?.items.length === 0 && (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-sm text-muted-foreground">
            <FileText className="h-10 w-10 opacity-50" />
            <p>No reports yet.</p>
            <p className="max-w-md text-center text-xs">
              In chat, after reviewing an audit you like, ask Tyrael for a
              report (e.g. "make a report for Cambridge about their email
              system") — the PDF lands here and gets sent to the chat
              automatically.
            </p>
          </div>
        )}

        {list.data && list.data.items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3">Audit</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Author</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((r) => <ReportRow key={r.id} r={r} />)}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function ReportRow({ r }: { r: Report }) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30">
      <td className="px-4 py-3 text-xs tabular-nums text-muted-foreground">
        {fmtDateTime(r.created_at)}
      </td>
      <td className="px-4 py-3 font-medium">{r.customer}</td>
      <td className="px-4 py-3">{r.title}</td>
      <td className="px-4 py-3 text-xs text-muted-foreground">{r.period ?? "—"}</td>
      <td className="px-4 py-3 text-xs">
        {r.audit_id !== null ? (
          <Link
            to={`/audits/${r.audit_id}`}
            className="inline-flex items-center gap-1 text-primary hover:underline"
            title={r.audit_teaser ?? undefined}
          >
            #{r.audit_id} <ExternalLink className="h-3 w-3" />
          </Link>
        ) : (
          <span className="text-muted-foreground">deleted</span>
        )}
      </td>
      <td className="px-4 py-3 text-xs tabular-nums text-muted-foreground">
        {fmtBytes(r.file_size_bytes)}
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground">
        {r.created_by_email ?? <span className="italic">backfilled</span>}
      </td>
      <td className="px-4 py-3 text-right">
        <a
          href={reportDownloadUrl(r.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          <Download className="h-3.5 w-3.5" /> PDF
        </a>
      </td>
    </tr>
  );
}
